/**
 * Shared kit for the Phase-11 quick-play games. Provides a canvas game loop
 * hook, a simple audio beep, a touch-swipe hook, and a common visual shell
 * (title / HUD chips / restart overlay).
 */
import { useEffect, useRef, useState } from "react";
import { Play, RotateCcw, Zap } from "lucide-react";
import AdReward from "@/embed/_adReward";

let audioCtx = null;
export function beep(freq = 660, dur = 0.15, type = "sine", vol = 0.12) {
  try {
    if (!audioCtx) {
      const AC = window.AudioContext || window.webkitAudioContext;
      audioCtx = new AC();
    }
    if (audioCtx.state === "suspended") audioCtx.resume();
    const o = audioCtx.createOscillator(); const g = audioCtx.createGain();
    o.type = type; o.frequency.value = freq;
    g.gain.setValueAtTime(vol, audioCtx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.0001, audioCtx.currentTime + dur);
    o.connect(g); g.connect(audioCtx.destination);
    o.start(); o.stop(audioCtx.currentTime + dur + 0.02);
  } catch {}
}

export function useGameLoop(canvasRef, draw, active = true) {
  const rafRef = useRef(0);
  const lastRef = useRef(0);
  const drawRef = useRef(draw);
  useEffect(() => { drawRef.current = draw; }, [draw]);
  useEffect(() => {
    const cvs = canvasRef.current;
    if (!cvs) return;
    const step = (ts) => {
      if (!lastRef.current) lastRef.current = ts;
      const dt = Math.min(0.05, (ts - lastRef.current) / 1000);
      lastRef.current = ts;
      const ctx = cvs.getContext("2d");
      drawRef.current({ ctx, w: cvs.width, h: cvs.height, dt });
      rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

export function useTouchSwipe(onSwipe) {
  useEffect(() => {
    let startX = 0, startY = 0;
    const ts = (e) => { const t = e.touches[0]; startX = t.clientX; startY = t.clientY; };
    const te = (e) => {
      const t = e.changedTouches[0];
      const dx = t.clientX - startX; const dy = t.clientY - startY;
      if (Math.abs(dx) > 30 && Math.abs(dx) > Math.abs(dy)) onSwipe(dx > 0 ? "right" : "left");
      else if (Math.abs(dy) > 30) onSwipe(dy > 0 ? "down" : "up");
    };
    window.addEventListener("touchstart", ts, { passive: true });
    window.addEventListener("touchend", te, { passive: true });
    return () => { window.removeEventListener("touchstart", ts); window.removeEventListener("touchend", te); };
  }, [onSwipe]);
}

/**
 * Common visual shell for all Phase-11 mini games.
 * Provides the header, canvas mount, HUD chips, and a restart overlay.
 */
export function GameShell({ canvasRef, title, accent = "#00F0FF", chips = [], instructions, gameOver, onRestart, aspect = "3 / 4", w = 480, h = 640, adGame }) {
  const cvsRef = useRef(null);
  useEffect(() => {
    // Wire the ref used by useGameLoop back into this canvas
    if (window.__jixxeoActiveCanvas) window.__jixxeoActiveCanvas.current = cvsRef.current;
  });
  return (
    <div className="min-h-screen bg-[#050510] text-white p-4 md:p-8 relative overflow-hidden flex flex-col items-center">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-3">
          <span className="w-10 h-10 rounded-xl flex items-center justify-center shadow-[0_0_20px_rgba(0,240,255,0.5)]" style={{ background: accent }}>
            <Zap className="w-5 h-5 text-black" strokeWidth={3} />
          </span>
          <div>
            <div className="mono text-[9px] uppercase tracking-[0.25em] text-[#00F0FF]">// jixxeo apps · demo pwa</div>
            <h1 className="font-heading font-black text-xl uppercase tracking-tighter leading-none">{title}</h1>
          </div>
        </div>
        <div className="flex flex-wrap gap-1.5 mb-3">
          {chips.map(([l, v]) => (
            <span key={l} className="mono text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full border" style={{ borderColor: `${accent}55`, background: `${accent}10`, color: accent }}>
              {l}: <span className="font-bold text-white ml-1">{v}</span>
            </span>
          ))}
        </div>
        <div className="relative rounded-2xl overflow-hidden border border-white/10 bg-black" style={{ aspectRatio: aspect }}>
          <canvas ref={canvasRef} width={w} height={h} className="w-full h-full touch-none select-none" />
          {gameOver && (
            <div className="absolute inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center">
              <div className="text-center px-4 space-y-3 max-w-xs">
                <div className="mono text-[10px] uppercase tracking-[0.3em] text-[#FFE300] mb-1">// {gameOver.title}</div>
                <div className="font-heading font-black text-3xl uppercase tracking-tighter mb-3">{gameOver.subtitle}</div>
                {adGame && (
                  <div className="flex justify-center">
                    <AdReward game={adGame} compact />
                  </div>
                )}
                <button onClick={onRestart} data-testid="game-restart"
                  className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white font-black mono text-xs uppercase tracking-widest px-6 py-3 rounded-full">
                  <RotateCcw className="w-4 h-4" /> Play again
                </button>
              </div>
            </div>
          )}
        </div>
        {instructions && (
          <div className="mono text-[10px] uppercase tracking-widest text-white/50 mt-3 text-center">{instructions}</div>
        )}
      </div>
    </div>
  );
}

/** Simple full-JSX shell for games that don't use a canvas. */
export function DomShell({ title, accent = "#00F0FF", chips = [], instructions, children }) {
  return (
    <div className="min-h-screen bg-[#050510] text-white p-4 md:p-8 relative overflow-hidden flex flex-col items-center">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-3">
          <span className="w-10 h-10 rounded-xl flex items-center justify-center shadow-[0_0_20px_rgba(0,240,255,0.5)]" style={{ background: accent }}>
            <Zap className="w-5 h-5 text-black" strokeWidth={3} />
          </span>
          <div>
            <div className="mono text-[9px] uppercase tracking-[0.25em] text-[#00F0FF]">// jixxeo apps · demo pwa</div>
            <h1 className="font-heading font-black text-xl uppercase tracking-tighter leading-none">{title}</h1>
          </div>
        </div>
        <div className="flex flex-wrap gap-1.5 mb-3">
          {chips.map(([l, v]) => (
            <span key={l} className="mono text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full border" style={{ borderColor: `${accent}55`, background: `${accent}10`, color: accent }}>
              {l}: <span className="font-bold text-white ml-1">{v}</span>
            </span>
          ))}
        </div>
        <div className="relative rounded-2xl overflow-hidden border border-white/10 bg-black/60 p-4">
          {children}
        </div>
        {instructions && <div className="mono text-[10px] uppercase tracking-widest text-white/50 mt-3 text-center">{instructions}</div>}
      </div>
    </div>
  );
}
